/**
 * Theme Toggle Component
 * Switches between dark and light themes
 */
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { Sun, Moon } from 'lucide-react';
import { motion } from 'framer-motion';

export const ThemeToggle = ({ className = '', size = 'default' }) => {
  const { theme, toggleTheme, isDark } = useTheme();

  return (
    <Button
      variant="ghost"
      size={size === 'sm' ? 'sm' : 'icon'}
      onClick={toggleTheme}
      className={`relative overflow-hidden ${className}`}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      data-testid="theme-toggle"
    >
      <motion.div
        initial={false}
        animate={{ rotate: isDark ? 0 : 180, scale: isDark ? 1 : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="absolute"
      >
        <Moon className="w-5 h-5 text-zinc-400" />
      </motion.div>
      <motion.div
        initial={false}
        animate={{ rotate: isDark ? -180 : 0, scale: isDark ? 0 : 1 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="absolute"
      >
        <Sun className="w-5 h-5 text-orange-400" />
      </motion.div>
      {/* Invisible placeholder for consistent size */}
      <Moon className="w-5 h-5 opacity-0" />
    </Button>
  );
};

// Compact toggle for tight spaces
export const ThemeToggleCompact = ({ className = '' }) => {
  const { theme, toggleTheme, isDark } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className={`w-12 h-6 rounded-full p-0.5 transition-colors ${
        isDark ? 'bg-zinc-700' : 'bg-orange-200'
      } ${className}`}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      data-testid="theme-toggle-compact"
    >
      <motion.div
        initial={false}
        animate={{ x: isDark ? 0 : 24 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className={`w-5 h-5 rounded-full flex items-center justify-center ${
          isDark ? 'bg-zinc-900' : 'bg-white'
        }`}
      >
        {isDark ? (
          <Moon className="w-3 h-3 text-zinc-400" />
        ) : (
          <Sun className="w-3 h-3 text-orange-500" />
        )}
      </motion.div>
    </button>
  );
};

export default ThemeToggle;
